import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Image,
  Platform,
  Animated,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { ActiveChargeAttributes } from '../../models/active-charges-model';
import * as Haptics from 'expo-haptics';

interface ActiveChargeMenuCardProps {
  activeCharges: ActiveChargeAttributes[];
  onPress: () => void;
}

const ActiveChargeMenuCard: React.FC<ActiveChargeMenuCardProps> = ({
  activeCharges,
  onPress,
}) => {
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const glowAnim = React.useRef(new Animated.Value(0)).current;

  // Calculate elapsed time
  const getElapsedTime = (dateCreated: string): string => {
    const createdDate = new Date(dateCreated);
    const now = new Date();
    const diffInMs = now.getTime() - createdDate.getTime();

    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(
      (diffInMs % (1000 * 60 * 60)) / (1000 * 60)
    );

    if (diffInHours > 0) {
      return `${diffInHours}hr, ${diffInMinutes} min`;
    }
    return `${diffInMinutes} min`;
  };

  // Update elapsed time periodically
  useEffect(() => {
    const updateElapsedTime = () => {
      const dateStr = activeCharges?.[0]?.order?.updatedAt ?? activeCharges?.[0]?.order?.createdAt;
      if (dateStr) {
        setElapsedTime(getElapsedTime(dateStr));
      }
    };

    updateElapsedTime();
    const intervalId = setInterval(updateElapsedTime, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [activeCharges]);

  // Pulsing glow animation
  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(glowAnim, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: false,
        }),
        Animated.timing(glowAnim, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: false,
        }),
      ])
    );
    pulse.start();
    return () => pulse.stop();
  }, [glowAnim]);

  // Platform-specific shadow animations
  const platformStyles = Platform.select({
    ios: {
      shadowOpacity: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0.3, 0.7],
      }),
      shadowRadius: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [5, 15],
      }),
    },
    android: {
      elevation: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [3, 10],
      }),
    },
  });

  const webStyle =
    Platform.OS === 'web'
      ? {
          boxShadow: glowAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [
              '0px 0px 5px rgba(57, 190, 183, 0.3)',
              '0px 0px 15px rgba(57, 190, 183, 0.7)',
            ],
          }),
        }
      : {};

  const activeCharge = activeCharges?.[0];
  if (!activeCharge) return null;
  const isPendingReconciliation = activeCharge.order?.status === 'PENDING_RECONCILIATION';

  const handlePress = () => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }
    onPress();
  };

  return (
    <Animated.View style={[
      styles.container,
      platformStyles,
      webStyle,
      isPendingReconciliation && { borderColor: '#FBD644' },
    ]}>
      <Pressable
        onPress={handlePress}
        style={({ pressed }) => [
          styles.pressable,
          { opacity: pressed ? 0.8 : 1 },
        ]}
      >
        <Image
          source={require('../../../assets/chargerStation.png')}
          style={styles.image}
        />
        <View style={styles.textContainer}>
          <Text style={styles.title}>
            {isPendingReconciliation ? 'Finalizing Session...' : 'Charging Session'}
          </Text>
          <Text style={styles.subtitle} numberOfLines={1}>
            {activeCharge.charger?.city ?? ''}, {activeCharge.charger?.state ?? ''}
          </Text>
        </View>
        <View style={styles.timeContainer}>
          {isPendingReconciliation ? (
            <MaterialIcons name="wifi-off" size={16} color="#FBD644" />
          ) : (
            <Text style={styles.timeText}>{elapsedTime}</Text>
          )}
          <MaterialIcons name="keyboard-arrow-right" size={20} color="#FFFFFF" />
        </View>
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginVertical: 12,
    backgroundColor: '#0B1E1D',
    borderColor: '#39BEB7',
    borderWidth: 2,
    borderRadius: 12,
    shadowColor: Platform.OS === 'ios' ? '#39BEB7' : undefined,
    shadowOffset: Platform.OS === 'ios' ? { width: 0, height: 0 } : undefined,
    elevation: Platform.OS === 'android' ? 5 : undefined,
  },
  pressable: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  image: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  textContainer: {
    flex: 1,
    marginLeft: 12,
  },
  title: {
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    color: '#8D8E90',
    marginTop: 2,
  },
  timeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  timeText: {
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    color: '#8D8E90',
  },
});

export default ActiveChargeMenuCard;

